import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, type ModelState } from '../api/client';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Card,
  ConfirmButton,
  JsonBlock,
  KeyValue,
  QueryBoundary,
  toneForModelState,
} from '../components/ui';
import { atLeast } from '../lib/roles';
import { dateTime } from '../lib/format';

// Mirrors services/ml/registry/lifecycle.py::_ALLOWED
const NEXT: Record<ModelState, ModelState[]> = {
  training: ['validated', 'rejected'],
  validated: ['paper_test', 'rejected', 'archived'],
  paper_test: ['approved', 'rejected', 'archived'],
  approved: ['archived', 'rejected'],
  rejected: [],
  archived: [],
};

export default function ModelDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const canPromote = atLeast(user?.role, 'admin');
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['model', id],
    queryFn: () => api.model(id as string),
    enabled: Boolean(id),
  });

  const promote = useMutation({
    mutationFn: ({ to, note }: { to: ModelState; note: string }) =>
      api.promoteModel(id as string, to, note || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['model', id] });
      qc.invalidateQueries({ queryKey: ['models'] });
    },
  });

  const m = q.data;
  const targets = m ? NEXT[m.state] : [];

  return (
    <>
      <PageHeader
        title={id ?? 'Model'}
        description="ML model record (ARCHITECTURE §13.5)."
        actions={
          <Link className="text-sm text-sky-400 hover:underline" to="/models">
            ← all models
          </Link>
        }
      />
      <QueryBoundary q={q}>
        {m && (
          <div className="space-y-5">
            <Card className="p-4">
              <KeyValue
                rows={[
                  ['State', <Badge tone={toneForModelState(m.state)}>{m.state}</Badge>],
                  ['Algorithm', m.algorithm],
                  ['Seed', m.seed],
                  ['Dataset', <Link className="text-sky-400 hover:underline" to={`/datasets/${m.dataset_hash}`}>{m.dataset_hash}</Link>],
                  ['Feature set', m.feature_set_version],
                  ['Features', `${m.feature_names.length} (${m.feature_names.slice(0, 6).join(', ')}${m.feature_names.length > 6 ? '…' : ''})`],
                  ['Created', dateTime(m.created_at_utc)],
                  ['State changed', `${dateTime(m.state_changed_at_utc)} by ${m.state_changed_by ?? '—'}`],
                  ['MLflow run', m.mlflow_run_id ?? '—'],
                ]}
              />
            </Card>

            {(canPromote && targets.length > 0) && (
              <Card className="p-4">
                <h2 className="mb-3 text-sm font-medium text-slate-300">Lifecycle</h2>
                <div className="flex flex-wrap gap-2">
                  {targets.map((to) => (
                    <ConfirmButton
                      key={to}
                      title={`Move ${id} to ${to}?`}
                      body={
                        to === 'approved'
                          ? 'APPROVED models are eligible for DEMO/LIVE. Metrics are frozen onto the record. This is audited against your account.'
                          : `This is an audited operator action.`
                      }
                      confirmLabel={`Move to ${to}`}
                      variant={to === 'approved' ? 'danger' : 'primary'}
                      needsReason
                      onConfirm={(note) => promote.mutateAsync({ to, note })}
                    >
                      → {to}
                    </ConfirmButton>
                  ))}
                </div>
              </Card>
            )}
            {!canPromote && (
              <p className="text-xs text-slate-500">Lifecycle changes require the admin role.</p>
            )}

            <Card className="p-4">
              <h2 className="mb-3 text-sm font-medium text-slate-300">Metrics</h2>
              <JsonBlock value={m.metrics} />
            </Card>

            {m.metrics_at_approval && (
              <Card className="p-4">
                <h2 className="mb-3 text-sm font-medium text-slate-300">Metrics at approval (frozen)</h2>
                <JsonBlock value={m.metrics_at_approval} />
              </Card>
            )}

            {m.rejection_reasons.length > 0 && (
              <Card className="border-red-900/60 p-4">
                <h2 className="mb-3 text-sm font-medium text-red-300">Rejection reasons</h2>
                <ul className="list-disc space-y-1 pl-5 text-sm text-red-200">
                  {m.rejection_reasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              </Card>
            )}

            <Card className="p-4">
              <h2 className="mb-3 text-sm font-medium text-slate-300">Params & library versions</h2>
              <JsonBlock value={{ params: m.params, library_versions: m.library_versions }} />
            </Card>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}
